import { useState, useEffect } from 'react';
import { Loader2, Check, Menu, X, Globe } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HeaderProps } from '../../types';
import { useAppContext } from '../../context/AppContext';
import { Language } from '../../types';
import { translatePath } from '../../utils/slugLookup';
import { FlagIcon } from '../ui/FlagIcon';
import { createQuoteTrackingContext } from '../../lib/quoteTracking';
import { MANAGED_PUBLIC_ASSET_SETTINGS, resolveManagedSettingUrl } from '../../lib/managedMedia';
import { getStaticPagePath, type UiLang } from '../../utils/routes';

export const Header = ({ activeLang, openQuoteModal, t, isLoading }: HeaderProps) => {
    const navText = t!.nav;
    const footerText = t!.footer;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [logoFailed, setLogoFailed] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const { uiLanguages, settings } = useAppContext();
    const logoUrl = resolveManagedSettingUrl(
        settings,
        MANAGED_PUBLIC_ASSET_SETTINGS.brandPrimaryLogo,
    );

    
    const currentLang: UiLang = location.pathname.startsWith('/lt') ? 'lt' : 'en';
    const servicesPath = getStaticPagePath('services', currentLang);
    const languagesPath = getStaticPagePath('languages', currentLang);
    const aboutPath = getStaticPagePath('about', currentLang);
    const newsPath = getStaticPagePath('news', currentLang);
    const faqPath = getStaticPagePath('faq', currentLang);
    const contactPath = getStaticPagePath('contact', currentLang);

    useEffect(() => {
        const handleScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        setLogoFailed(false);
    }, [logoUrl]);



    const onLanguageSelect = async (newLang: Language) => {
        const targetLang = newLang.code.toLowerCase();
        
        
        if (targetLang === currentLang) {
            setIsMenuOpen(false);
            return;
        }
        try {
            
            const newPath = await translatePath(location.pathname, targetLang);
            void navigate(newPath);
        } catch (error) {
            console.error('Error switching language:', error);
            
            const segments = location.pathname.split('/').filter(Boolean);
            if (segments.length > 0 && segments[0].length === 2) {
                segments[0] = targetLang;
            } else {
                segments.unshift(targetLang);
            }
            void navigate(`/${segments.join('/')}`);
        } finally {
            setIsMenuOpen(false);
        }
    };

    return (
        <header className={`fixed w-full z-50 transition-all duration-300 ${scrolled ? 'bg-white/95 backdrop-blur-md shadow-md py-2' : 'bg-white py-4 shadow-sm'}`}>
            <div className="container mx-auto px-4 md:px-6 flex justify-between items-center">
                <Link to={getStaticPagePath('home', currentLang)} className="flex items-center cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                    {logoUrl && !logoFailed ? (
                        <img
                            src={logoUrl}
                            alt="Littera Magna Logo"
                            width="153"
                            height="56"
                            className={`transition-all duration-300 object-contain ${scrolled ? 'h-10' : 'h-14 md:h-16'}`}
                            onError={() => setLogoFailed(true)}
                        />
                    ) : (
                        <div className={`flex w-[153px] items-center justify-center rounded-lg bg-stone-200 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500 transition-all duration-300 ${scrolled ? 'h-10' : 'h-14 md:h-16'}`}>
                            LM
                        </div>
                    )}
                </Link>

                <nav className="hidden md:flex items-center space-x-8">
                    <Link to={getStaticPagePath('home', currentLang)} className="text-stone-600 hover:text-green-700 text-sm font-semibold uppercase tracking-wider transition-colors">
                        {navText.home}
                    </Link>
                    <Link to={servicesPath} className="text-stone-600 hover:text-green-700 text-sm font-semibold uppercase tracking-wider transition-colors">
                        {navText.services}
                    </Link>
                    <Link to={languagesPath} className="text-stone-600 hover:text-green-700 text-sm font-semibold uppercase tracking-wider transition-colors">
                        {navText.languages}
                    </Link>

                    <Link to={aboutPath} className="text-stone-600 hover:text-green-700 text-sm font-semibold uppercase tracking-wider transition-colors">
                        {footerText.aboutUs}
                    </Link>
                    <Link to={newsPath} className="text-stone-600 hover:text-green-700 text-sm font-semibold uppercase tracking-wider transition-colors">
                        {navText.news}
                    </Link>
                    <Link to={faqPath} className="text-stone-600 hover:text-green-700 text-sm font-semibold uppercase tracking-wider transition-colors">
                        {footerText.faq}
                    </Link>
                    <Link to={contactPath} className="text-stone-600 hover:text-green-700 text-sm font-semibold uppercase tracking-wider transition-colors">
                        {navText.contact}
                    </Link>
                </nav>

                <div className="hidden md:flex items-center space-x-4">
                    <div className="relative group">
                        <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-stone-200 hover:border-green-300 hover:bg-green-50 transition-all">
                            {isLoading ? (
                                <Loader2 size={16} className="animate-spin text-green-700" />
                            ) : (
                                <>
                                    <Globe size={16} className="text-green-600" />
                                    <FlagIcon code={activeLang.code} size="md" />
                                </>
                            )}
                        </button>
                        <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-2xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 overflow-hidden border border-stone-100">
                            <div className="px-3 py-2 bg-stone-50 border-b border-stone-100">
                                <span className="text-xs font-medium text-stone-400 uppercase tracking-wider">{navText.selectLanguage}</span>
                            </div>
                            {uiLanguages.map(lang => (
                                <button
                                    key={lang.code}
                                    onClick={() => void onLanguageSelect(lang)}
                                    className={`w-full text-left px-4 py-3 text-sm hover:bg-green-50 flex items-center justify-between transition-colors ${
                                        activeLang.code === lang.code ? 'bg-green-50' : ''
                                    }`}
                                >
                                    <span className="flex items-center gap-3">
                                        <FlagIcon code={lang.code} size="md" />
                                        <span className="text-stone-700">{lang.name}</span>
                                    </span>
                                    {activeLang.code === lang.code && <Check size={16} className="text-stone-500" strokeWidth={2.5} />}
                                </button>
                            ))}
                        </div>
                    </div>

                    <button
                        onClick={() => {
                            const tracking = createQuoteTrackingContext({
                                entrypoint: 'header_quote',
                                surface: 'header',
                            });
                            void import('../../lib/analytics').then(({ trackQuoteCtaClick }) => {
                                trackQuoteCtaClick(tracking);
                            });
                            openQuoteModal({ tracking });
                        }}
                        className="bg-green-700 text-white px-6 py-2.5 rounded shadow-lg shadow-green-900/10 font-medium text-sm hover:bg-green-800 transition-all hover:-translate-y-0.5"
                    >
                        {navText.quote}
                    </button>
                </div>

                <button
                    onClick={() => setIsMenuOpen(!isMenuOpen)}
                    className="md:hidden text-stone-700"
                    aria-label={navText.toggleMenu}
                >
                    {isMenuOpen ? <X /> : <Menu />}
                </button>
            </div>

            {isMenuOpen && (
                <div className="absolute top-full left-0 w-full bg-white shadow-xl border-t border-stone-100 p-4 flex flex-col space-y-4 md:hidden animate-fade-in-down">
                    <Link to={getStaticPagePath('home', currentLang)} className="text-stone-700 font-medium py-2 border-b border-stone-50 uppercase text-sm tracking-wider" onClick={() => setIsMenuOpen(false)}>
                        {navText.home}
                    </Link>
                    <Link to={servicesPath} className="text-stone-700 font-medium py-2 border-b border-stone-50 uppercase text-sm tracking-wider" onClick={() => setIsMenuOpen(false)}>
                        {navText.services}
                    </Link>
                    <Link to={languagesPath} className="text-stone-700 font-medium py-2 border-b border-stone-50 uppercase text-sm tracking-wider" onClick={() => setIsMenuOpen(false)}>
                        {navText.languages}
                    </Link>

                    <Link to={aboutPath} className="text-stone-700 font-medium py-2 border-b border-stone-50 uppercase text-sm tracking-wider" onClick={() => setIsMenuOpen(false)}>
                        {footerText.aboutUs}
                    </Link>
                    <Link to={newsPath} className="text-stone-700 font-medium py-2 border-b border-stone-50 uppercase text-sm tracking-wider" onClick={() => setIsMenuOpen(false)}>
                        {navText.news}
                    </Link>
                    <Link to={faqPath} className="text-stone-700 font-medium py-2 border-b border-stone-50 uppercase text-sm tracking-wider" onClick={() => setIsMenuOpen(false)}>
                        {footerText.faq}
                    </Link>
                    <Link to={contactPath} className="text-stone-700 font-medium py-2 border-b border-stone-50 uppercase text-sm tracking-wider" onClick={() => setIsMenuOpen(false)}>
                        {navText.contact}
                    </Link>

                    <div className="flex flex-wrap gap-2 py-2">
                        {uiLanguages.map(lang => (
                            <button
                                key={lang.code}
                                onClick={() => void onLanguageSelect(lang)}
                                className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-colors ${
                                    activeLang.code === lang.code 
                                        ? 'bg-green-50 border-green-300 shadow-sm' 
                                        : 'bg-stone-50 border-stone-200 hover:bg-stone-100'
                                }`}
                            >
                                <FlagIcon code={lang.code} size="sm" />
                            </button>
                        ))}
                    </div>
                    <button
                        onClick={() => {
                            const tracking = createQuoteTrackingContext({
                                entrypoint: 'mobile_header_quote',
                                surface: 'mobile_header',
                            });
                            void import('../../lib/analytics').then(({ trackQuoteCtaClick }) => {
                                trackQuoteCtaClick(tracking);
                            });
                            openQuoteModal({ tracking });
                            setIsMenuOpen(false);
                        }}
                        className="w-full bg-green-700 text-white py-3 rounded font-bold shadow-lg"
                    >
                        {navText.quoteMobile}
                    </button>
                </div>
            )}
        </header>
    );
};
